/**
 * BidDigi Operator Dashboard — API client
 * ----------------------------------------
 * Thin fetch wrapper + bearer-token handling for the operator/admin API served by
 * packages/config-backend (same origin as the existing admin `x-api-key` routes — the backend
 * treats a valid operator session token as equivalent to that shared key).
 *
 * Base URL follows the same "one default + override" pattern already used in this monorepo for
 * API base URLs (see packages/wrapper/src/biddigi-remote-config.js's configApiBase()): a hardcoded
 * production default, overridable via a Vite env var for local/staging builds, and further
 * overridable via a plain global for the Playwright verify script without needing a real build
 * flag.
 */

const DEFAULT_API_BASE = 'https://biddigi-config-backend.biddigi25.workers.dev';

export function apiBase() {
  if (typeof window !== 'undefined' && window.__BIDDIGI_API_BASE__) return window.__BIDDIGI_API_BASE__;
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) {
    return import.meta.env.VITE_API_BASE;
  }
  return DEFAULT_API_BASE;
}

const TOKEN_KEY = 'biddigi_operator_token';

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down contexts (private mode, disabled site data). The session
    // just won't persist across reloads in that case — not fatal to the current page's usage.
  }
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('Could not reach the BidDigi API. Check your connection and try again.', 0, null);
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const message = (data && data.error) || `Request failed (HTTP ${res.status})`;
    throw new ApiError(message, res.status, data);
  }

  return data;
}

function qs(params) {
  const clean = {};
  Object.keys(params || {}).forEach((k) => {
    if (params[k] !== undefined && params[k] !== null && params[k] !== '') clean[k] = params[k];
  });
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
}

export const api = {
  // --- Auth ---------------------------------------------------------------------------------
  login: (email, password) =>
    request('/api/operator/login', { method: 'POST', body: { email, password }, auth: false }),
  me: () => request('/api/operator/me'),

  // --- Stats / overview -----------------------------------------------------------------------
  overview: (from, to) => request(`/api/admin/stats/overview${qs({ from, to })}`),
  demandPartnerStats: (from, to) => request(`/api/admin/stats/demand-partners${qs({ from, to })}`),
  // Can each enabled partner's declared delivery signal actually fire? See
  // config-backend/src/lib/deliverySignalHealth.js -- this is what decides whether revenue
  // attributed to a partner may be called verified, or is only partner-reported.
  deliverySignalHealth: (days) => request(`/api/admin/stats/delivery-signal-health${qs({ days })}`),

  // --- Diagnostics ----------------------------------------------------------------------------
  // The four reports that answer "why is the network performing the way it is", as opposed to the
  // configuration surfaces above. Every one of them existed as an admin JSON endpoint with no
  // reader for weeks; see claude/adg636-null-result-and-the-win-to-delivered-ratio-2026-10-01.md
  // for what that cost. supply-path-health in particular has been open item #5 in
  // claude/supply-chain-transparency-spo-status.md since 2026-09-16 ("No UI. The sweep is JSON on
  // an admin endpoint"), and an unpublished seller id sat live for six hours because nobody could
  // see it.
  //
  // `days` rather than from/to: these four take a rolling window, unlike the dated overview above.
  partnerOutcomes: (days) => request(`/api/admin/stats/partner-outcomes${qs({ days })}`),
  demandAttribution: (days) => request(`/api/admin/stats/demand-attribution${qs({ days })}`),
  inboundContract: (days) => request(`/api/admin/stats/inbound-contract${qs({ days })}`),
  supplyPathHealth: () => request('/api/schain/supply-path-health'),

  // --- Publishers -----------------------------------------------------------------------------
  listPublishers: () => request('/api/publishers'),
  createPublisher: (body) => request('/api/publishers', { method: 'POST', body }),
  getPublisher: (id) => request(`/api/publishers/${id}`),
  patchPublisher: (id, body) => request(`/api/publishers/${id}`, { method: 'PATCH', body }),
  deletePublisher: (id, hard) => request(`/api/publishers/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
  getPublisherSettings: (id) => request(`/api/publishers/${id}/settings`),
  patchPublisherSettings: (id, body) => request(`/api/publishers/${id}/settings`, { method: 'PATCH', body }),

  // --- Demand partners --------------------------------------------------------------------------
  listDemandPartners: () => request('/api/demand-partners'),
  // Workstream 2: self-serve creation for rows that reuse an already-registered generic
  // connector (currently connector_type: 'generic_ortb') -- see the POST route's own comment for
  // why this doesn't extend to a brand-new bespoke connector.
  createDemandPartner: (body) => request('/api/demand-partners', { method: 'POST', body }),
  patchDemandPartner: (code, body) => request(`/api/demand-partners/${code}`, { method: 'PATCH', body }),

  // --- Partner onboarding + verification (migrations 039, 074) --------------------------------
  // readiness is a pure inspection of the stored declarations -- it makes no network call and
  // proves nothing about the endpoint. verifyDemandPartner is the one that actually presses it:
  // a real OpenRTB request with test:1, a $1,000,000 floor and a reserved .invalid bundle, so
  // it cannot produce a billable impression and is never recorded as an auction.
  demandPartnerReadiness: () => request('/api/demand-partners/readiness'),
  partnerVerifications: (code) => request(`/api/demand-partners/${code}/verifications`),
  verifyDemandPartner: (code, which) =>
    request(`/api/demand-partners/${code}/verify${qs({ check: which })}`, { method: 'POST' }),

  // --- Commercial rules (migration 073) -------------------------------------------------------
  // Append-only: there is no update and no delete, because the terms that applied to an
  // already-settled period must not be editable after the fact.
  commercialRules: (scopeType, scopeKey) => request(`/api/commercial-rules${qs({ scopeType, scopeKey })}`),
  commercialRuleCoverage: (from, to) => request(`/api/commercial-rules/coverage${qs({ from, to })}`),
  addCommercialRule: (body) => request('/api/commercial-rules', { method: 'POST', body }),
  // Undo for a self-serve row created by mistake -- the server only allows this when the partner
  // is disabled and has zero real usage/stats history (see the DELETE route's own comment).
  deleteDemandPartner: (code) => request(`/api/demand-partners/${code}`, { method: 'DELETE' }),
  listPublisherDemandPartners: (publisherId) => request(`/api/publishers/${publisherId}/demand-partners`),
  putPublisherDemandPartner: (publisherId, code, body) =>
    request(`/api/publishers/${publisherId}/demand-partners/${code}`, { method: 'PUT', body }),
  // Demand Connectivity & Management workstream: "which publishers use this partner" (reverse
  // lookup, no credentials in the response) for the combined config+health view.
  listDemandPartnerPublishers: (code) => request(`/api/demand-partners/${code}/publishers`),

  // --- Bidders (client-side Prebid.js) -----------------------------------------------------------
  // Server-side demand (demand_partners, above) and client-side Prebid.js bidders (bidders/
  // publisher_bidders/placement_bidder_params) are two separate mechanisms — see
  // docs/BIDDIGI_PLATFORM_ARCHITECTURE.md section 3. These reuse the same admin API
  // packages/dashboard's BiddersTab already called; no backend change needed.
  listBidders: () => request('/api/bidders'),
  listPublisherBidders: (publisherId) => request(`/api/publishers/${publisherId}/bidders`),
  putPublisherBidder: (publisherId, code, body) =>
    request(`/api/publishers/${publisherId}/bidders/${code}`, { method: 'PUT', body }),
  putPlacementBidderParams: (placementId, code, params) =>
    request(`/api/placements/${placementId}/bidder-params/${code}`, { method: 'PUT', body: { params } }),

  // --- A/B tests ------------------------------------------------------------------------------
  listAbTests: (publisherId) => request(`/api/publishers/${publisherId}/ab-tests`),
  createAbTest: (publisherId, body) => request(`/api/publishers/${publisherId}/ab-tests`, { method: 'POST', body }),
  updateAbTest: (testId, body) => request(`/api/ab-tests/${testId}`, { method: 'PATCH', body }),

  // --- Placements & floors ---------------------------------------------------------------------
  listPlacements: (publisherId) => request(`/api/publishers/${publisherId}/placements`),
  createPlacement: (publisherId, body) => request(`/api/publishers/${publisherId}/placements`, { method: 'POST', body }),
  getPlacement: (id) => request(`/api/placements/${id}`),
  patchPlacement: (id, body) => request(`/api/placements/${id}`, { method: 'PATCH', body }),
  deletePlacement: (id) => request(`/api/placements/${id}`, { method: 'DELETE' }),
  putPlacementDemandPartnerParams: (id, code, params) =>
    request(`/api/placements/${id}/demand-partner-params/${code}`, { method: 'PUT', body: { params } }),
  // Demand Connectivity & Management workstream: read-back for the override above (key names
  // only, never values -- see the backend route's own comment).
  listPlacementDemandPartnerParams: (id) => request(`/api/placements/${id}/demand-partner-params`),

  listPublisherFloors: (publisherId) => request(`/api/publishers/${publisherId}/floors`),
  putPublisherFloor: (publisherId, mediaType, body) =>
    request(`/api/publishers/${publisherId}/floors/${mediaType}`, { method: 'PUT', body }),
  putPlacementFloor: (placementId, mediaType, body) =>
    request(`/api/placements/${placementId}/floors/${mediaType}`, { method: 'PUT', body }),

  // --- House Ads (guaranteed-fill fallback, served only on a real no-fill) -------------------
  // Task #111: one independent fallback creative per format (banner/video/native) per scope --
  // `:format` is part of the URL (see config-backend/src/routes/houseAds.js's own header for why
  // it's never read from the body), so every call below takes `format` as an explicit argument.
  listHouseAds: (publisherId) => request(`/api/publishers/${publisherId}/house-ads`),
  putPublisherHouseAd: (publisherId, format, body) =>
    request(`/api/publishers/${publisherId}/house-ads/default/${format}`, { method: 'PUT', body }),
  deletePublisherHouseAd: (publisherId, format) =>
    request(`/api/publishers/${publisherId}/house-ads/default/${format}`, { method: 'DELETE' }),
  putPlacementHouseAd: (placementId, format, body) =>
    request(`/api/placements/${placementId}/house-ad/${format}`, { method: 'PUT', body }),
  deletePlacementHouseAd: (placementId, format) =>
    request(`/api/placements/${placementId}/house-ad/${format}`, { method: 'DELETE' }),

  // --- Inbound seats ------------------------------------------------------------------------
  listInboundSeats: () => request('/api/inbound-seats'),
  createInboundSeat: (body) => request('/api/inbound-seats', { method: 'POST', body }),
  getInboundSeat: (seatId) => request(`/api/inbound-seats/${seatId}`),
  patchInboundSeat: (seatId, body) => request(`/api/inbound-seats/${seatId}`, { method: 'PATCH', body }),

  // --- QPS governance (migrations 069 + 070) --------------------------------------------------
  // One call returns both control surfaces: the configured caps (from Postgres, authoritative)
  // and the live token-bucket telemetry (proxied server-side from auction-service's
  // OPS_API_KEY-guarded /ops/qps -- that key must never reach this bundle). The response always
  // carries every seat and partner; `live: null` on a row means telemetry was unreadable, which
  // is deliberately distinct from a live reading of zero.
  // Backed by GET /api/ops/qps -- config-backend/src/routes/opsQps.js and its worker twin.
  opsQps: (windowMinutes) => request(`/api/ops/qps${qs({ window_minutes: windowMinutes })}`),

  // --- Reporting feed health (migration 071) --------------------------------------------------
  // Per-partner import freshness, recent sync runs, and the revisions worth a human's attention
  // (figures that went DOWN, or stopped being readable). Backed by
  // GET /api/admin/reporting-health -- config-backend/src/lib/reportingHealth.js.
  reportingHealth: () => request('/api/admin/reporting-health'),

  // --- Operator roles (migration 072) ---------------------------------------------------------
  // Admin-only: a 403 here is the role model working, not a fault. setOperatorRole changes a
  // role WITHOUT resetting a password or revoking sessions -- the upsert above does both, which
  // is why re-scoping needed its own endpoint.
  listOperatorAccounts: () => request('/api/operator-accounts'),
  setOperatorRole: (id, role) => request(`/api/operator-accounts/${id}`, { method: 'PATCH', body: { role } }),
  rotateInboundSeatKey: (seatId) => request(`/api/inbound-seats/${seatId}/rotate-key`, { method: 'POST' }),
  // Paginated + searchable (same shape as listSeatBundleFloors below) -- a bulk-onboarded seat
  // (Cuedart, Pumpkin Ads) can carry hundreds of thousands of sources, so this never fetches
  // "all of them" in one call.
  listSeatSources: (seatId, { limit, offset, search } = {}) =>
    request(`/api/inbound-seats/${seatId}/sources${qs({ limit, offset, search })}`),
  createSeatSource: (seatId, body) => request(`/api/inbound-seats/${seatId}/sources`, { method: 'POST', body }),
  patchSeatSource: (id, body) => request(`/api/inbound-seat-sources/${id}`, { method: 'PATCH', body }),
  deleteSeatSource: (id) => request(`/api/inbound-seat-sources/${id}`, { method: 'DELETE' }),

  // --- Bulk supply onboarding (Workstream 3 -- Cuedart/Pumpkin Ads bundle-floor bulk import) ----
  // See SeatBundleFloorsPage.jsx and config-backend's src/importers/bundleSupplyImport.js for the
  // full design. dryRun defaults true server-side when the query param is omitted, but this client
  // always passes it explicitly so the call site's intent (preview vs. commit) is never ambiguous.
  listSeatBundleFloors: (seatId, { limit, offset, search } = {}) =>
    request(`/api/inbound-seats/${seatId}/bundle-floors${qs({ limit, offset, search })}`),
  listSeatBundleImportBatches: (seatId) => request(`/api/inbound-seats/${seatId}/bundle-floors/import-batches`),
  runSeatBundleImport: (seatId, body, { dryRun, force } = {}) =>
    request(`/api/inbound-seats/${seatId}/bundle-floors/import${qs({ dryRun, force })}`, { method: 'POST', body }),

  // --- Configuration versions (Control Plane DRAFT -> VALIDATE -> PUBLISH -> ROLLBACK) --------
  // Reuses packages/config-backend's existing configVersions.js API unchanged — no new backend
  // route, no schema change. See components/publisher/ConfigVersionsTab.jsx for the UI and its
  // own safety notes on what "publish" actually does to a real publisher's live Prebid.js config.
  listConfigVersions: (publisherId) => request(`/api/publishers/${publisherId}/configuration-versions`),
  createConfigVersionDraft: (publisherId) =>
    request(`/api/publishers/${publisherId}/configuration-versions`, { method: 'POST' }),
  getConfigVersion: (id) => request(`/api/configuration-versions/${id}`),
  getActiveConfigVersion: (publisherId) => request(`/api/publishers/${publisherId}/configuration-versions/active`),
  validateConfigVersion: (id) => request(`/api/configuration-versions/${id}/validate`, { method: 'POST' }),
  publishConfigVersion: (id) => request(`/api/configuration-versions/${id}/publish`, { method: 'POST' }),
  rollbackConfigVersion: (publisherId, sourceId) =>
    request(`/api/publishers/${publisherId}/configuration-versions/${sourceId}/rollback`, { method: 'POST' }),

  // --- Audit log ------------------------------------------------------------------------------
  auditLog: (params) => request(`/api/audit-log${qs(params)}`),

  // --- Auction events (P0-2 ledger, see claude/world-class-readiness-architecture-review.md) --
  auctionEvents: (params) => request(`/api/admin/auction-events${qs(params)}`),
  // Auction Log per-partner diagnostics (P1 finding #10, timeout/error/no-bid/floor-rejection
  // detail) -- lazily fetched only when a specific Auction Log row is expanded, see
  // AuctionLogPage.jsx.
  auctionEventPartnerResults: (id) => request(`/api/admin/auction-events/${id}/partner-results`),

  // --- Operator accounts ------------------------------------------------------------------------
  upsertOperatorAccount: (body) => request('/api/operator-accounts', { method: 'POST', body }),

  // --- Supply Quality & Transparency (schain) --------------------------------------------------
  // See config-backend's src/routes/schainPolicies.js -- schain_policies is keyed to exactly one
  // of publisher_id or inbound_seat_id (never both), enforced both here and server-side.
  listSchainPolicies: () => request('/api/schain-policies'),
  createSchainPolicy: (body) => request('/api/schain-policies', { method: 'POST', body }),
  patchSchainPolicy: (id, body) => request(`/api/schain-policies/${id}`, { method: 'PATCH', body }),
  deleteSchainPolicy: (id) => request(`/api/schain-policies/${id}`, { method: 'DELETE' }),
  // "Preview Outbound schain" -- the mandate's explicitly-named feature. Calls the SAME
  // buildOutboundSchain() the real auction path uses; see that route's own header comment.
  previewSchain: (body) => request('/api/schain/preview', { method: 'POST', body }),

  // --- Supply Quality & Transparency (seller accounts / sellers.json) -------------------------
  listSellerAccounts: () => request('/api/seller-accounts'),
  createSellerAccount: (body) => request('/api/seller-accounts', { method: 'POST', body }),
  patchSellerAccount: (id, body) => request(`/api/seller-accounts/${id}`, { method: 'PATCH', body }),
  deleteSellerAccount: (id) => request(`/api/seller-accounts/${id}`, { method: 'DELETE' }),
  // The real, live, public sellers.json -- unauthenticated by design (see
  // routes/supplyAuthorizations.js's header), so this dashboard fetches it exactly like any
  // outside verifier would, to confirm what's actually being served.
  fetchSellersJson: () => request('/sellers.json', { auth: false }),

  // --- Supply Quality & Transparency (supply authorizations: ads.txt/app-ads.txt) --------------
  listSupplyAuthorizations: ({ identity_type, status, publisher_id, inbound_seat_id } = {}) =>
    request(`/api/supply-authorizations${qs({ identity_type, status, publisher_id, inbound_seat_id })}`),
  createSupplyAuthorization: (body) => request('/api/supply-authorizations', { method: 'POST', body }),
  patchSupplyAuthorization: (id, body) => request(`/api/supply-authorizations/${id}`, { method: 'PATCH', body }),
  deleteSupplyAuthorization: (id) => request(`/api/supply-authorizations/${id}`, { method: 'DELETE' }),
  // A real network fetch of the supply source's OWN ads.txt/app-ads.txt -- operator-triggered
  // only (see the route's own comment: never on the auction path).
  recheckSupplyAuthorization: (id) => request(`/api/supply-authorizations/${id}/recheck`, { method: 'POST' }),
  validateSupplyAuthorizationBatch: (records) =>
    request('/api/supply-authorizations/validate-batch', { method: 'POST', body: { records } }),

  // --- Supply Quality & Transparency (IVT / traffic quality) ------------------------------------
  listTrafficQualityProviders: () => request('/api/traffic-quality-providers'),
  createTrafficQualityProvider: (body) => request('/api/traffic-quality-providers', { method: 'POST', body }),
  patchTrafficQualityProvider: (code, body) => request(`/api/traffic-quality-providers/${code}`, { method: 'PATCH', body }),

  listIvtRules: () => request('/api/ivt-rules'),
  patchIvtRule: (code, body) => request(`/api/ivt-rules/${code}`, { method: 'PATCH', body }),

  // publisher_id omitted -> network-wide default rows; passed -> that publisher's own overrides
  // only (never merged with defaults server-side -- see the route's own comment).
  listIvtPolicies: (publisherId) => request(`/api/ivt-policies${qs({ publisher_id: publisherId })}`),
  putIvtPolicy: (body) => request('/api/ivt-policies', { method: 'PUT', body }),

  trafficQualitySummary: ({ from, to, publisherId } = {}) => request(`/api/traffic-quality/summary${qs({ from, to, publisherId })}`),
  trafficQualityBreakdown: ({ dimension, from, to } = {}) => request(`/api/traffic-quality/breakdown${qs({ dimension, from, to })}`),
  trafficQualityProviderStats: ({ from, to } = {}) => request(`/api/traffic-quality/providers${qs({ from, to })}`),
  trafficQualityDiagnostics: (auctionEventId) => request(`/api/traffic-quality/diagnostics/${auctionEventId}`),
};
